import type { FC } from 'react';
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router';

import { UserAPIs } from '@/api';
import { logout } from '@/api/base/auth';
import { useTemplateListQuery } from '@/api/queries/template';
import type { TagWithCount } from '@/api/types/tag';
import type { TemplateListItem, TemplateListResponse, TemplateQuery } from '@/api/types/template';
import type { User } from '@/api/types/user';
import { UserType } from '@/api/types/user';
import { AccountSettingsModal } from '@/common/account-settings-modal';
import { AdminControlsModal } from '@/common/admin-controls-modal';
import { AllTemplatesIcon } from '@/common/all-templates-icon';
import { EmptyIcon } from '@/common/empty-icon';
import { FloatingPanel, FloatingPanelItem } from '@/common/floating-panel';
import { IntaglioIcon } from '@/common/intaglio-icon';
import { Navbar } from '@/common/navbar';
import { QuickHelp } from '@/common/quick-help';
import { SelectSizeModal } from '@/common/select-size-modal';
import { TemplateTile } from '@/common/template-tile';
import { UserMenu } from '@/common/user-menu';

export const CreateCreative: FC = () => {
    const [selectedTag, setSelectedTag] = useState<TagWithCount | null>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<TemplateListItem | null>(null);
    const [isSelectSizeModalOpen, setIsSelectSizeModalOpen] = useState<boolean>(false);
    const [isAccountSettingsOpen, setIsAccountSettingsOpen] = useState<boolean>(false);
    const [isAdminControlsOpen, setIsAdminControlsOpen] = useState<boolean>(false);
    const [templateList, setTemplateList] = useState<TemplateListItem[]>([]);
    const [count, setCount] = useState<number>(0);
    const [templateTags, setTemplateTags] = useState<TagWithCount[]>([]);
    const [user, setUser] = useState<User | null>(null);
    const [query, setQuery] = useState<TemplateQuery>({ draft: false });
    const navigate = useNavigate();

    const updateSelectedTag = (tag: TagWithCount | null) => {
        setSelectedTag(tag);
    };

    const { refetch: refetchTemplateList } = useTemplateListQuery(query, {
        onSuccess: (data: TemplateListResponse) => {
            setTemplateList(data.results);
            setCount(data.totalCount - data.draftCount);
            setTemplateTags(data.tags);
        },
    });

    const fetchUser = useCallback((refetch: boolean) => {
        UserAPIs.getCurrentUser(refetch).then((response) => {
            setUser(response);
        });
    }, []);

    useEffect(() => {
        if (selectedTemplate) {
            setIsSelectSizeModalOpen(true);
        }
    }, [selectedTemplate]);

    useEffect(() => {
        if (!isSelectSizeModalOpen) {
            setSelectedTemplate(null);
        }
    }, [isSelectSizeModalOpen]);

    useEffect(() => {
        fetchUser(false);
    }, [fetchUser]);

    useEffect(() => {
        if (selectedTag) {
            setQuery((prev) => ({ ...prev, tag: selectedTag?.id }));
        } else {
            setQuery({ draft: false });
        }
    }, [selectedTag]);

    return (
        <div className="flex flex-col h-screen w-screen bg-black-50">
            <Navbar
                leading={
                    <Link to="/">
                        <IntaglioIcon />
                    </Link>
                }
                trailing={
                    <div className="flex flex-row gap-2.5 items-center">
                        <QuickHelp />
                        <div className="h-6 border-gray-100 border-l-[1px]" />
                        <UserMenu
                            user={user}
                            onLogout={() => {
                                logout();
                                navigate('/login', { replace: true });
                            }}
                            onAccountSettings={() => setIsAccountSettingsOpen(true)}
                            onOrganizationSettings={() => setIsAdminControlsOpen(true)}
                        />
                    </div>
                }
            />
            <p className="text-white text-3xl font-normal text-center">What do you want to create?</p>
            <div className="flex min-h-0 flex-row h-full mt-4">
                <FloatingPanel omitSeperator={true} className="px-4 h-full w-[250px] flex-shrink-0 overflow-y-auto">
                    {[
                        <FloatingPanelItem key="all-templates" className="flex flex-col gap-2">
                            <div
                                onClick={count > 0 ? () => updateSelectedTag(null) : () => { }}
                                onKeyUp={
                                    count > 0
                                        ? (e) => {
                                            if (e.key === 'Enter') updateSelectedTag(null);
                                        }
                                        : () => { }
                                }
                                className={`flex flex-row justify-between items-center rounded-md px-2.5 py-2 ${selectedTag ? '' : 'bg-gray-100'}`}
                            >
                                <div className="flex flex-row gap-2 items-center">
                                    <AllTemplatesIcon className="w-4 h-4 stroke-white" />{' '}
                                    <span className="text-white text-sm font-normal">All Templates</span>
                                </div>
                                <p
                                    className={`flex h-6 w-6 bg-gray-100 rounded-md ${count > 0 ? 'text-white' : 'text-gray-110'} text-xs text-center justify-center items-center`}
                                >
                                    {count}
                                </p>
                            </div>
                            {templateTags
                                .filter((tag) => tag.count > 0)
                                .map((templateTag, index) => (
                                    <div
                                        onClick={() => updateSelectedTag(templateTag)}
                                        onKeyUp={(e) => e.key === 'Enter' && updateSelectedTag(templateTag)}
                                        className={`flex flex-row justify-between items-center rounded-md px-2.5 py-2 ${selectedTag?.id !== templateTag.id ? '' : 'bg-gray-100'}`}
                                        key={templateTag.id}
                                    >
                                        <div className="flex flex-row gap-2 items-center">
                                            <div className="w-4 h-4" />{' '}
                                            <span className="text-white text-sm font-normal">{templateTag.name}</span>
                                        </div>
                                        <p
                                            className={`flex h-6 w-6 bg-gray-100 rounded-md ${templateTag.count > 0 ? 'text-white' : 'text-gray-110'} text-xs text-center justify-center items-center`}
                                        >
                                            {templateTag.count}
                                        </p>
                                    </div>
                                ))}
                        </FloatingPanelItem>,
                    ]}
                </FloatingPanel>
                <div className="border-r-[1px] border-gray-120 h-[65vh]" />
                <div className="flex flex-col flex-grow overflow-hidden">
                    {templateList.length === 0 && (
                        <div className="flex flex-col items-center justify-center flex-grow">
                            <EmptyIcon className="h-32 w-28" />
                        </div>
                    )}
                    <div className="min-h-0 overflow-y-scroll flex-1 no-scrollbar px-4 pb-4">
                        {templateList.length > 0 && (
                            <div className="mt-5 no-scrollbar">
                                <div className="grid gap-6 justify-stretch grid-cols-[repeat(auto-fill,_minmax(300px,_1fr))] max-sm:grid-cols-1">
                                    {templateList.map((template) => (
                                        <TemplateTile
                                            key={template.id}
                                            title={template.name}
                                            hideCreatedAt={true}
                                            showFrameCount={true}
                                            frameCount={template.variantCount}
                                            createdAt={template.createdAt}
                                            tags={template.tags}
                                            thumbnail={template.publishedVersion.thumbnail}
                                            onClick={() => setSelectedTemplate(template)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <SelectSizeModal
                isOpen={isSelectSizeModalOpen}
                setIsOpen={setIsSelectSizeModalOpen}
                templateId={selectedTemplate?.id ?? -1}
                onDone={(creativeId: number) => {
                    setIsSelectSizeModalOpen(false);
                    navigate(`/creatives/generate/${creativeId}`);
                }}
            />
            {user?.type === UserType.ADMIN && (
                <AdminControlsModal
                    isOpen={isAdminControlsOpen}
                    setIsOpen={setIsAdminControlsOpen}
                    onAccountUpdated={() => fetchUser(true)}
                />
            )}
            <AccountSettingsModal
                isOpen={isAccountSettingsOpen}
                setIsOpen={setIsAccountSettingsOpen}
                onAccountUpdated={() => fetchUser(true)}
            />
        </div>
    );
};
